/**
 * Turn a thrown API error into a sentence a workshop user can act on (#2716).
 *
 * `ApiError.message` is the HTTP status line — `super(`${status} ${statusText}`)`
 * in api.ts — so it is never Finnish and never a sentence. The codebase was
 * full of
 *
 *     error instanceof Error ? error.message : t('somethingFailed')
 *
 * which reads like a safe fallback but is not: `ApiError` *is* an `Error`, so
 * the ternary always takes the raw branch and the translated string is dead
 * code. Users saw `500 Internal Server Error`, and on one path that status
 * line was the entire body of the toast.
 *
 * What this returns instead, in order of preference:
 *
 * 1. **A translated `code` or `reason`** from the server body. Server tokens
 *    are stable identifiers (`lift_unavailable`, `another_timer_active`,
 *    `empty_line_name`, `STALE_ROW`), so they translate cleanly; the lookup is case-insensitive
 *    and lives under `common:errorCode.*` unless the caller names another
 *    prefix. A transition refusal that names the current status
 *    (`invalid_status_transition` + `currentStatus`) says which status.
 * 2. **A sentence for the HTTP status** — a refused permission, an expired
 *    session, a record that is gone, a clash or a server error each say so
 *    (#3352: the toast must tell the user *why*). A 5xx quotes the request id
 *    when the server echoed one, so support can find it in the logs.
 * 3. **The caller's own fallback**, for everything else.
 *
 * What it never returns is the status line, and never the server's raw
 * `message` — that field carries English Zod text like "String must contain
 * at least 1 character(s)", which is for developers, not mechanics.
 */

import type { TFunction } from 'i18next';
import type { ApiError } from './api';

/**
 * Duck-typed on purpose: many component tests mock `@/lib/api` with a bare
 * `{ api }` factory, and an `instanceof` against a missing export throws
 * inside the very catch block this helper serves. A numeric `status` on an
 * Error is what api.ts throws and all we read.
 */
function asApiError(error: unknown): ApiError | null {
  return error instanceof Error && typeof (error as ApiError).status === 'number'
    ? (error as ApiError)
    : null;
}

/** Server error bodies we know how to read. */
interface ApiErrorBody {
  code?: unknown;
  reason?: unknown;
  currentStatus?: unknown;
  error?: unknown;
  message?: unknown;
}

function bodyOf(error: unknown): ApiErrorBody | null {
  const apiError = asApiError(error);
  if (!apiError) return null;
  const { data } = apiError;
  return typeof data === 'object' && data !== null ? (data as ApiErrorBody) : null;
}

/** Body `code` the staff-api global error handler sends for transient DB/pool failures (#2945). */
export const TEMPORARILY_UNAVAILABLE = 'temporarily_unavailable';

/** Default i18n prefix for server tokens: `common:errorCode.<token>`. */
const DEFAULT_CODE_PREFIX = 'errorCode';

/** True for a 503 that is a transient outage, not a feature that is switched off. */
export function isTemporarilyUnavailable(error: unknown): boolean {
  return apiErrorCode(error) === TEMPORARILY_UNAVAILABLE;
}

/** The server's machine-readable code, when it sent one. */
export function apiErrorCode(error: unknown): string | null {
  const code = bodyOf(error)?.code;
  return typeof code === 'string' && code !== '' ? code : null;
}

/**
 * The server's `reason`, which some routes send instead of a `code`
 * (`another_timer_active` on the clock routes).
 */
export function apiErrorReason(error: unknown): string | null {
  const reason = bodyOf(error)?.reason;
  return typeof reason === 'string' && reason !== '' ? reason : null;
}

/** Status-derived key under `common:errors.*`, or null when we have nothing better. */
function statusKey(status: number): string | null {
  if (status === 401) return 'errors.sessionExpired';
  if (status === 403) return 'errors.actionForbidden';
  if (status === 404) return 'errors.notFoundAction';
  if (status === 409) return 'errors.conflict';
  if (status === 423) return 'errors.locked';
  if (status === 429) return 'errors.tooManyRequests';
  if (status >= 500) return 'errors.serverError';
  return null;
}

/** A translation that exists, or null — i18next echoes a missing key back. */
function lookup(t: TFunction, key: string, vars?: Record<string, string>): string | null {
  const translated = vars ? t(key, vars) : t(key);
  return translated !== key ? translated : null;
}

/**
 * The server tokens to try, in order: as sent (`WORK_ORDER_SIGNATURE_REQUIRED`
 * keeps its uppercase key), then lowercased (`STALE_ROW` → `stale_row`). The
 * transition refusal is split by whether the server named the current status.
 */
function serverTokens(error: unknown): { tokens: string[]; status?: string } | null {
  const raw = apiErrorCode(error) ?? apiErrorReason(error);
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (lower !== 'invalid_status_transition') {
    return { tokens: lower === raw ? [raw] : [raw, lower] };
  }
  const current = bodyOf(error)?.currentStatus;
  return typeof current === 'string' && current !== ''
    ? { tokens: [lower], status: current }
    : { tokens: ['invalid_state'] };
}

export interface DescribeOptions {
  /**
   * i18n prefix for server `code` / `reason` values. Defaults to
   * `'errorCode'`, the shared map in the common namespace; pass e.g.
   * `'invoices:errorCode'` where a feature keeps its own.
   *
   * An unmapped token must never reach the screen, so a key that resolves to
   * itself is discarded; that is the same discipline
   * WhatsAppTemplateCreateDialog already applies.
   */
  codePrefix?: string;
  /** Shown when nothing more specific is known. Required — no silent generic. */
  fallback: string;
}

/**
 * @param t  a `common`-bound TFunction (namespaced keys still work through it)
 */
export function describeApiError(
  error: unknown,
  t: TFunction,
  options: DescribeOptions,
): string {
  const { codePrefix = DEFAULT_CODE_PREFIX, fallback } = options;

  const server = serverTokens(error);
  if (server) {
    // The status label lives in the workorders namespace; fall back to the
    // raw status rather than hiding which status refused the action.
    const vars = server.status !== undefined
      ? { status: lookup(t, `workorders:status.${server.status}`) ?? server.status }
      : undefined;
    for (const token of server.tokens) {
      const translated = lookup(t, `${codePrefix}.${token}`, vars);
      if (translated) return translated;
    }
  }

  // The global error handler marks transient DB/pool failures (#2945); say
  // "try again" instead of a generic server error.
  if (apiErrorCode(error) === TEMPORARILY_UNAVAILABLE) {
    const translated = lookup(t, 'errors.temporarilyUnavailable');
    if (translated) return translated;
  }

  const apiError = asApiError(error);
  if (apiError) {
    if (apiError.status >= 500 && apiError.requestId) {
      const withId = lookup(t, 'errors.serverErrorWithId', { id: apiError.requestId });
      if (withId) return withId;
    }
    const key = statusKey(apiError.status);
    if (key) {
      const translated = lookup(t, key);
      if (translated) return translated;
    }
  }

  // A genuine network failure: fetch rejects with a TypeError, never an
  // ApiError, so the user is offline or the server is unreachable.
  if (error instanceof TypeError) {
    const translated = lookup(t, 'errors.network');
    if (translated) return translated;
  }

  return fallback;
}
